import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Alert from '@mui/material/Alert';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import Spinner from '../../../../components/Spinner';
import DashboardService, { DashboardSummary, DashboardActionItems } from '../../../../services/dashboard.service';
import { Modules, AuthRoutes } from '../../../../enums';

const ACTION_ITEMS_POLL_MS = 60_000;

const formatTrendDate = (isoDate: string) =>
  new Date(isoDate).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });

type ActionSectionKey =
  | 'expiringSoon'
  | 'expired'
  | 'pendingPayment'
  | 'mandatoryConfigMissing'
  | 'failedProvisioning';

const ACTION_SECTION_SEVERITY: Record<ActionSectionKey, 'warning' | 'error'> = {
  expiringSoon: 'warning',
  expired: 'error',
  pendingPayment: 'warning',
  mandatoryConfigMissing: 'warning',
  failedProvisioning: 'error',
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [trendDays, setTrendDays] = useState<30 | 90>(30);
  const [trendLoading, setTrendLoading] = useState(false);
  const [actionItems, setActionItems] = useState<DashboardActionItems | null>(null);
  const actionItemsPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const planPalette = useMemo(
    () => [theme.palette.primary.main, theme.palette.primary.light, theme.palette.text.secondary, theme.palette.text.disabled],
    [theme]
  );

  useEffect(() => {
    setLoading(true);
    DashboardService.getSummary(30)
      .then((response: any) => setSummary(response?.data ?? null))
      .finally(() => setLoading(false));
  }, []);

  // US-034 AC5: silently refresh the Needs Action cards every 60s while the dashboard is open.
  useEffect(() => {
    const load = () => DashboardService.getActionItems().then((response: any) => setActionItems(response?.data ?? null));
    load();
    actionItemsPollRef.current = setInterval(load, ACTION_ITEMS_POLL_MS);
    return () => {
      if (actionItemsPollRef.current) clearInterval(actionItemsPollRef.current);
    };
  }, []);

  const handleTrendToggle = useCallback((_e: unknown, value: 30 | 90 | null) => {
    if (!value) return;
    setTrendDays(value);
    setTrendLoading(true);
    DashboardService.getSummary(value)
      .then((response: any) => {
        setSummary((prev) => (prev ? { ...prev, trend: response?.data?.trend ?? [] } : response?.data ?? null));
      })
      .finally(() => setTrendLoading(false));
  }, []);

  const handleCountCardClick = useCallback(
    (statusId: number) => {
      navigate(`${Modules.tenants}?status=${statusId}`);
    },
    [navigate]
  );

  const handleActionCardClick = useCallback(
    (sectionKey: ActionSectionKey) => {
      navigate(`${Modules.home}${AuthRoutes.renewals}#section-${sectionKey}`);
    },
    [navigate]
  );

  const allActionSections: { key: ActionSectionKey; title: string; count: number }[] = actionItems
    ? [
        { key: 'expiringSoon' as const, title: t('renewalRadar.pageTexts.sections.expiringSoon'), count: actionItems.expiringSoon.count },
        { key: 'expired' as const, title: t('renewalRadar.pageTexts.sections.expired'), count: actionItems.expired.count },
        {
          key: 'pendingPayment' as const,
          title: t('renewalRadar.pageTexts.sections.pendingPayment'),
          count: actionItems.pendingPayment.count,
        },
        {
          key: 'mandatoryConfigMissing' as const,
          title: t('renewalRadar.pageTexts.sections.mandatoryConfigMissing'),
          count: actionItems.mandatoryConfigMissing.count,
        },
        {
          key: 'failedProvisioning' as const,
          title: t('renewalRadar.pageTexts.sections.failedProvisioning'),
          count: actionItems.failedProvisioning.count,
        },
      ]
    : [];
  const actionSections = allActionSections.filter((section) => section.count > 0);

  if (loading || !summary) {
    return (
      <div className="text-center py-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h4 className="m-0">{t('dashboard.pageTexts.header.title')}</h4>
        <p className="text-muted m-0">{t('dashboard.pageTexts.header.subTitle')}</p>
      </div>

      <div className="row g-4 mb-4">
        {summary.statusCounts.length === 0 && (
          <div className="col-12 text-muted">{t('dashboard.pageTexts.counts.empty')}</div>
        )}
        {summary.statusCounts.map((row) => (
          <div className="col-md-2" key={row.statusId}>
            <div
              className="card h-100 cursor-pointer"
              style={{ borderTop: `4px solid ${row.statusColour}` }}
              onClick={() => handleCountCardClick(row.statusId)}
              role="button"
              tabIndex={0}
            >
              <div className="card-body">
                <div className="text-muted small">{row.statusName}</div>
                <div className="fs-2 fw-bold">{row.count}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {actionSections.length > 0 && (
        <div className="mb-4">
          <h6 className="mb-2">{t('dashboard.pageTexts.needsAction.title')}</h6>
          <div className="row g-3">
            {actionSections.map((section) => (
              <div className="col-md-3" key={section.key}>
                <Alert
                  severity={ACTION_SECTION_SEVERITY[section.key]}
                  className="cursor-pointer"
                  onClick={() => handleActionCardClick(section.key)}
                >
                  <strong>{section.count}</strong> {section.title}
                </Alert>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="row g-4 mb-4">
        <div className="col-md-6">
          <div className="card h-100">
            <h6 className="card-header">{t('dashboard.pageTexts.byProduct.title')}</h6>
            <hr className="m-0" />
            <div className="card-body" style={{ height: 280 }}>
              {summary.byProduct.length === 0 ? (
                <div className="text-muted text-center pt-6">{t('dashboard.pageTexts.byProduct.empty')}</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.byProduct}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="productName" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {summary.byProduct.map((row) => (
                        <Cell key={row.productId} fill={row.themeColour ?? theme.palette.text.disabled} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card h-100">
            <h6 className="card-header">{t('dashboard.pageTexts.byPlan.title')}</h6>
            <hr className="m-0" />
            <div className="card-body" style={{ height: 280 }}>
              {summary.byPlan.length === 0 ? (
                <div className="text-muted text-center pt-6">{t('dashboard.pageTexts.byPlan.empty')}</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summary.byPlan}
                      dataKey="count"
                      nameKey="planName"
                      innerRadius="55%"
                      outerRadius="80%"
                      paddingAngle={2}
                    >
                      {summary.byPlan.map((row, index) => (
                        <Cell key={row.planId} fill={planPalette[index % planPalette.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="m-0">{t('dashboard.pageTexts.trend.title')}</h6>
          <ToggleButtonGroup size="small" value={trendDays} exclusive onChange={handleTrendToggle}>
            <ToggleButton value={30}>{t('dashboard.pageTexts.trend.toggle30')}</ToggleButton>
            <ToggleButton value={90}>{t('dashboard.pageTexts.trend.toggle90')}</ToggleButton>
          </ToggleButtonGroup>
        </div>
        <hr className="m-0" />
        <div className="card-body" style={{ height: 300 }}>
          {trendLoading ? (
            <div className="text-center pt-6">
              <Spinner />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={summary.trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatTrendDate} tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip labelFormatter={(value) => formatTrendDate(String(value))} />
                <Line
                  type="monotone"
                  dataKey="count"
                  name={t('dashboard.pageTexts.trend.seriesName')}
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
